import { type Document } from '@contentful/rich-text-types';

import { type CMS_LOCALES } from './constants';

import { type SupportedLocation } from '@/modules/location/constants';

export type Nullable<Content> = Content | null;

export type CMSLocales = CMS_LOCALES;

export type ContentfulItemCollection<T> = {
  total?: number;
  skip?: number;
  limit?: number;
  items: Array<T | null>;
};

export type Brand = {
  id: number;
  name: string;
  slug: string;
};

export type SeoMeta = {
  metaTitle?: string;
  metaDescription?: string;
  canonicalLink?: string;
};

export type CMSCategory = {
  name: string;
  id: number;
  slug: string;
  parentId?: number | null;
  children?: number[];
};

export type CMSBrandCategoryPage = {
  title?: string;
  copy?: string;
  seo?: SeoMeta;
};

export type LegacyCMSCategoryPage = {
  category: CMSCategory;
  title?: string;
  copy?: string;
  seo?: SeoMeta;
};

export type PageMetadata = {
  title?: string;
  copy?: string;
  seo?: SeoMeta;
};

export type CMSPageMetadataCollectionResponse = {
  data: Record<
    string,
    {
      items?: PageMetadata[];
    }
  >;
};

export type BrandBySlugResponse = {
  id: number;
  name: string;
  slug: string;
  copy?: string;
  seo?: SeoMeta;
};

export type HomePageModuleImage = {
  url?: string;
  width?: number;
  height?: number;
};

export type HomepageModuleTile = {
  title?: string;
  subtitle?: string;
  url?: string | null;
  image?: HomePageModuleImage | null;
  image2?: HomePageModuleImage | null;
  image3?: HomePageModuleImage | null;
};

export type HomepageModuleResponse = {
  data: {
    webHomepageBlock: {
      cta: {
        copy?: string;
        url?: string;
        requiresAuth?: boolean;
      } | null;
      titleCopy: string | null;
      tileCollection: ContentfulItemCollection<HomepageModuleTile>;
    } | null;
  };
};

export type PopularBrandsBlockImage = {
  url: string;
  width: number;
  height: number;
};

export type PopularBrandsBlockItem = {
  title: string;
  webUrl: string;
  brandImagesCollection: ContentfulItemCollection<PopularBrandsBlockImage>;
};

export type PopularBrandsModuleResponse = {
  data: {
    popularBrandsBlock: {
      title: string;
      brandListCollection: ContentfulItemCollection<PopularBrandsBlockItem>;
    } | null;
  };
};

export type TransformedWebBannerResponse = {
  mobileBackgroundImageUrl: string;
  mobileBackgroundImageUrl2?: string;
  mobileBackgroundImageUrl3?: string;
  desktopBackgroundImageUrl: string;
  desktopBackgroundImageUrl2?: string;
  desktopBackgroundImageUrl3?: string;
  heading: string;
  bodyJson: Document;
  ctaHref: string;
  ctaCopy: string;
};

export type WebBannerResponseItem = {
  heading: string;
  body: {
    json: Document;
  };
  cta: {
    copy: string;
    url: string;
    requiresAuth?: boolean;
  };
  desktopBackgroundImage: {
    url: string;
  };
  desktopBackgroundImage2?: {
    url: string;
  };
  desktopBackgroundImage3?: {
    url: string;
  };
  mobileBackgroundImage: {
    url: string;
  };
  mobileBackgroundImage2?: {
    url: string;
  };
  mobileBackgroundImage3?: {
    url: string;
  };
  regionsEnabled: Partial<Record<SupportedLocation, boolean>>;
  theme?: {
    headingColour?: string;
    subHeadingColour?: string;
    headingFont?: string;
    subHeadingFont?: string;
    headingFontStyle?: string;
    subHeadingFontStyle?: string;
  };
};

export type WebBannerResponse = {
  data: {
    webBannerCollection: ContentfulItemCollection<WebBannerResponseItem>;
  };
};

export type CMSExplorePageResponse = {
  data: {
    webExplorePageResultsCollection: ContentfulItemCollection<
      CMSExplorePage & {
        searchQuery: string;
      }
    >;
  };
};

export type CMSExplorePageBubbleNavItem = {
  image?: {
    url: string;
  };
  url?: string;
  title?: string;
};

export type CMSExplorePage = {
  heading: string;
  slug: string;
  description: string;
  searchQuery: string;
  desktopBackgroundImage: {
    url: string;
    width: number;
    height: number;
  };
  mobileBackgroundImage: {
    url: string;
    width: number;
    height: number;
  };
  cta?: {
    copy: string;
    url: string;
  };
  brands?: {
    brandsCollection: {
      items: { brand: string }[];
    };
  };
  categorySizeRestrictions?: {
    categoriesCollection: {
      items: { category: number }[];
    };
  };
  hideCategoryFilters: boolean;
  headerModule?: {
    itemsCollection: {
      items: Array<CMSExplorePageBubbleNavItem>;
    };
  };
  featuredShops?: {
    title?: string;
    showTitle: boolean;
    shops: string[];
  };
  seoIndex?: boolean | null;
  seoMetaTitle?: string | null;
  seoMetaDescription?: string | null;
};

export type InfluencerDrop = {
  dropDate: string;
  countdownDate: string;
  endDate: string;
  timeZone: string;
  dropInfo: string | null;
  notificationDate: string | null;
  triggerNotification: boolean | null;
};

export type InfluencerDropResponse = {
  data: {
    influencerCollection: {
      items: Array<{
        username: string;
        bio: string;
        useInfluencerLayout: boolean;
        instagramFollowers: number;
        tikTokFollowers: number;
        youTubeFollowers: number;
        depopFollowers: number;
        desktopBanner: { url: string };
        mobileBanner: { url: string };
        dropsCollection: {
          items: Array<InfluencerDrop>;
        };
      }>;
    };
  };
};

export enum BannerLocations {
  Home = 'Home',
  SellingHub = 'SellingHub',
}

export const UTM_TAGS = {
  BLENDED: 'blended',
  W_18_24: 'women_18_24',
  W_25_35: 'women_25_35',
  M_18_24: 'men_18_24',
} as const;

export type EbayUtmTag = (typeof UTM_TAGS)[keyof typeof UTM_TAGS];
